import { Col, ModalV2, P4PDayGiftBadge, Row, Spinner2, Text } from 'components';
import { getP4PDaySelfEnrolment, theme } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

/**
 * The fan ID, sized to be read across a booth counter.
 *
 * Monospace with wide tracking because staff retype it from the fan's screen, and the alphabet
 * it is drawn from was chosen for the same reason. Scales down on narrow screens rather than
 * overflowing — the phone is the device this is actually opened on.
 */
const FanId = styled.div`
  font-family: monospace;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 6px;
  /* Tracking adds a trailing gap after the last character; this re-centres the block. */
  text-indent: 6px;
  color: ${theme.GREY_90};
  background-color: ${theme.GREY_10};
  border-radius: 12px;
  padding: 16px 20px;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  word-break: break-all;

  @media (max-width: 480px) {
    font-size: 26px;
    letter-spacing: 4px;
    text-indent: 4px;
    padding: 14px 12px;
  }
`;

/**
 * Turns the English gift name stored on the campaign into a locale key: "Key Chain" -> keyChain.
 *
 * The campaign holds English because a sponsor can add a tier at any point and English reads
 * correctly the moment they do; the translation catches up at the next release. Lookup failures
 * are therefore expected and not an error — the caller falls back to the English it was given.
 *
 * Kept in sync with playaz4playaz-mobile/src/components/P4PDayFanGiftModal.tsx.
 */
function giftItemKey(name) {
  return String(name)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)?/g, (_, next) => (next ? next.toUpperCase() : ''));
}

/**
 * What a fan sees when they tap their own P4P Day badge: the badge, which one of its colour they
 * hold, what it is redeemed for, and the ID booth staff look them up by.
 *
 * Fetched on every open rather than read from the signed-in user in redux. The number and the ID
 * are frozen on the user record, but the badge's name, colour and gift are the sponsor's to
 * change, and a fan who claimed before an edit should see the badge as it is now.
 *
 * Mirrors playaz4playaz-mobile/src/components/P4PDayFanGiftModal.tsx; keep the two in step.
 */
const P4PDayFanGiftModal = ({ visible, close }) => {
  const [loading, setLoading] = useState(true),
    [enrolment, setEnrolment] = useState(null),
    { isMobile } = useSelector((state) => state.appState),
    { t } = useTranslation();

  useEffect(() => {
    if (!visible) return undefined;

    let active = true;
    setLoading(true);

    getP4PDaySelfEnrolment()
      .then((data) => active && setEnrolment(data))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [visible]);

  if (!visible) return null;

  // Every part of the sentence comes from the server; none of it is assembled from a letter or
  // a hardcoded palette, so a sponsor rename reaches a badge that was issued before the change.
  const hasBadge =
    !!enrolment?.giftColor &&
    Number.isFinite(enrolment.badgeNo) &&
    Number.isFinite(enrolment.giftTotal) &&
    !!enrolment.giftColorName;

  return (
    <ModalV2
      visible={visible}
      closeModal={close}
      width="420px"
      header={
        <Text h4 bold marg="0">
          {t('p4pDay.giftTitle')}
        </Text>
      }
    >
      <Col centerAll wid="100%" pad={isMobile ? '20px 16px' : '24px 20px'}>
        {loading ? (
          <Col centerAll wid="100%" ht="180px">
            <Spinner2 col={theme.SECONDARY} />
          </Col>
        ) : !enrolment?.enrolled ? (
          <Text med col={theme.GREY_60} style={{ textAlign: 'center' }}>
            {t('p4pDay.notEnrolled')}
          </Text>
        ) : (
          <>
            {hasBadge ? (
              <>
                <Row noFlex centerAll marg="0 0 16px">
                  <P4PDayGiftBadge giftColor={enrolment.giftColor} badgeNo={enrolment.badgeNo} />
                </Row>

                <Text med bold col={theme.GREY_90} style={{ textAlign: 'center', lineHeight: 1.5 }}>
                  {t('p4pDay.badgeOfTotal', {
                    badgeNo: enrolment.badgeNo,
                    total: enrolment.giftTotal,
                    color: enrolment.giftColorName
                  })}
                </Text>

                {!!enrolment.giftItem && (
                  <Text
                    med
                    col={theme.GREY_60}
                    marg="8px 0 0"
                    style={{ textAlign: 'center', lineHeight: 1.5 }}
                  >
                    {t('p4pDay.giftItemLabel')}
                    {': '}
                    {/* Falls back to the server's English when this build has no translation
                        for a gift the sponsor added after it shipped. */}
                    {t(`p4pDay.giftItems.${giftItemKey(enrolment.giftItem)}`, {
                      defaultValue: enrolment.giftItem
                    })}
                  </Text>
                )}
              </>
            ) : (
              <Text med col={theme.GREY_60} style={{ textAlign: 'center', lineHeight: 1.5 }}>
                {t('p4pDay.noGift')}
              </Text>
            )}

            <Text b2 col={theme.GREY_60} marg="24px 0 8px">
              {t('p4pDay.fanIdLabel')}
            </Text>

            <FanId>{enrolment.fanId || '—'}</FanId>

            
          </>
        )}
      </Col>
    </ModalV2>
  );
};

export default P4PDayFanGiftModal;
